import React from 'react';
import { motion } from 'framer-motion';
import { Badge } from '@/components/ui';
import { GBPPostSuggestion } from '@/data/mocks/local';
import {
  FileText,
  Image as ImageIcon,
  Link,
  Calendar,
  Globe
} from 'lucide-react';

interface GBPPostSuggestionsProps {
  suggestions: GBPPostSuggestion[];
}

export const GBPPostSuggestions: React.FC<GBPPostSuggestionsProps> = ({ suggestions }) => {
  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'event':
        return <Calendar className="w-4 h-4" />;
      case 'offer':
        return <FileText className="w-4 h-4" />;
      case 'news':
        return <Globe className="w-4 h-4" />;
      case 'product':
        return <ImageIcon className="w-4 h-4" />;
      default:
        return <FileText className="w-4 h-4" />;
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {suggestions.map((post, index) => (
        <motion.div
          key={post.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.1 }}
          className="p-4 border rounded-lg hover:border-blue-500 hover:shadow-md transition-all"
        >
          <div className="flex items-center justify-between mb-3">
            <Badge variant="outline" className="flex items-center gap-1">
              <Globe className="w-3 h-3" />
              {post.language.toUpperCase()}
            </Badge>
            <Badge variant="outline" className="flex items-center gap-1">
              {getTypeIcon(post.type)}
              {post.type}
            </Badge>
          </div>

          <h4 className="font-medium mb-2 line-clamp-2">{post.title}</h4>
          <p className="text-sm text-gray-600 mb-4 line-clamp-3">
            {post.content}
          </p>

          {post.image && (
            <div className="mb-4">
              <div className="relative h-32 bg-gray-100 rounded-md overflow-hidden">
                <ImageIcon className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-gray-400" />
              </div>
            </div>
          )}

          {post.cta && (
            <div className="flex items-center justify-between mt-auto">
              <a
                href={post.cta.url}
                className="text-sm text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1"
              >
                {post.cta.text}
                <Link className="w-3 h-3" />
              </a>
            </div>
          )}
        </motion.div>
      ))}
    </div>
  );
};
